import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Section, Field } from "../../shared/components/EditorFields"
import { CATEGORY_ICONS, CATEGORY_COLORS } from "../types"

export function CategoryFields({
  category, set,
}: {
  category: any
  set: (patch: Record<string, any>) => void
}) {
  const icon: string | undefined = category.icon || undefined
  const color: string | undefined = category.color || undefined

  return (
    <Section title="Map appearance" defaultOpen>
      <Field
        label="Pin icon"
        hint="Without one, this category's services show a blank pin on the public map."
      >
        {/* Names, not glyphs: the Material Symbols font is not loaded in this app */}
        <div className="grid grid-cols-2 gap-1">
          {CATEGORY_ICONS.map(name => (
            <button
              key={name}
              type="button"
              aria-pressed={icon === name}
              // Clicking the selected icon clears it
              onClick={() => set({ icon: icon === name ? undefined : name })}
              className={cn(
                "truncate rounded border px-2 py-1.5 text-left text-xs hover:bg-muted/60",
                icon === name && "border-primary bg-primary/10 font-medium",
              )}
              title={name}
            >
              {name}
            </button>
          ))}
        </div>
        {/* Legacy or imported value the site does not know */}
        {icon && !CATEGORY_ICONS.includes(icon as any) && (
          <p className="text-[11px] text-amber-700 dark:text-amber-400">
            "{icon}" is not one of the site's icons, so the pin renders blank. Pick one above.
          </p>
        )}
      </Field>

      <Field label="Colour">
        <div className="flex flex-wrap gap-1">
          {CATEGORY_COLORS.map(hex => (
            <button
              key={hex}
              type="button"
              aria-label={hex}
              aria-pressed={color === hex}
              onClick={() => set({ color: color === hex ? undefined : hex })}
              className={cn(
                "h-6 w-6 rounded border",
                color === hex ? "ring-2 ring-primary ring-offset-1" : "border-border",
              )}
              style={{ backgroundColor: hex }}
              title={hex}
            />
          ))}
        </div>
        <div className="flex items-center gap-2 pt-1">
          <span className="text-[11px] text-muted-foreground">
            {color ?? "No colour set"}
          </span>
          {(color || icon) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-[11px]"
              onClick={() => set({ color: undefined, icon: undefined })}
            >
              Clear both
            </Button>
          )}
        </div>
      </Field>
    </Section>
  )
}
